/**
 * ELEMNT — CTA «IR AL CARRITO» tras un alta exitosa (producto principal)
 * ---------------------------------------------------------------------------------
 * Encargo "Complete The Look + Cart Flow Refinement" (2026-09-08) § 5.
 *
 * 🔴 ALCANCE — SOLO el botón de "Añadir al carrito" del formulario PRINCIPAL de la PDP.
 *
 * Se distingue de Complete The Look (y de cualquier product-card / quick-add) con la MISMA
 * señal que ya usan `elemnt-fit-required.js` y `elemnt-sticky-purchase.js`: la sección que
 * contiene el `<variant-picker data-template-product-match="true">` de la plantilla. El
 * Body de "Completa tu outfit" vive en su PROPIA `.shopify-section`, sin ese picker
 * (`sections/elemnt-complete-the-look.liquid`), así que nunca entra en el alcance de este
 * módulo — su botón sigue diciendo siempre «AGREGAR AL CARRITO» (encargo §§ 4, 8, 9). La
 * barra pegajosa tampoco: su botón es un `<button ref="addToCartButton">` suelto, sin
 * `<add-to-cart-component>` alrededor (`sections/product-information.liquid`), así que el
 * selector de este módulo no lo alcanza — queda fuera de este encargo (§ 13, "sticky
 * purchase" en la lista de "no tocar").
 *
 * ── Por qué el interruptor es `type` + `on:click`, no un guardia de `submit` ──────────
 *
 * La primera idea era interceptar el `click` en fase de captura, como hace la capa 2 de
 * `elemnt-fit-required.js`. Se descartó: `component.js` registra SU delegación de
 * `on:click` también en `document` con `{ capture: true }` (`assets/component.js`,
 * `registerEventListeners`), y el orden entre dos listeners de captura del MISMO evento en
 * el MISMO nodo lo decide el orden de REGISTRO — que ocurre en el `connectedCallback` de
 * cada `Component`, no en la carga de este archivo. Apostar a que este módulo se registra
 * primero es exactamente el tipo de suposición de temporización fragil que el encargo pide
 * evitar (§ 6).
 *
 * En su lugar, el estado "recién añadido" cambia dos atributos reales del botón:
 *   · `type="button"` — apaga el envío nativo del formulario (nada que interceptar);
 *   · se retira `on:click="/handleClick"` — con eso fuera, `getElement()` en
 *     `component.js` no encuentra ningún elemento con ese atributo al subir por
 *     `closest()`, así que `AddToCartComponent.handleClick` (la animación fly-to-cart, el
 *     reintento de alta) simplemente nunca se dispara. Cero carrera con Horizon.
 * Un listener propio, normal, atado UNA vez a este botón, hace el resto: abrir el carrito.
 *
 * ── Por qué NO hace falta deshacer nada "a mano" tras un cambio de Horma o Talla ──────
 *
 * `ProductFormComponent#onProductSelect` (`assets/product-form.js`) hace
 * `morph(currentAddToCartButton, newAddToCartButton)` en cada cambio de variante, con el
 * HTML que sirve el servidor — `type="submit"`, `on:click="/handleClick"`, el texto
 * original. `assets/morph.js` reconcilia atributos completos (añade los que faltan, QUITA
 * los que sobran) y sincroniza nodos de texto por valor — así que un cambio de variante
 * borra este estado por completo, sin que este módulo tenga que escuchar `productSelect` ni
 * guardar nada. Es la MISMA garantía que ya usa el resto de la PDP.
 *
 * Lo único que un morph de variante NO dispara es un cambio de CANTIDAD sobre la MISMA
 * variante — por eso este módulo sí escucha `quantity-selector:update` (`@theme/events`) y
 * revierte el estado ahí: si el cliente ya añadió y luego sube la cantidad a 2 pensando en
 * agregar una unidad más, el botón tiene que volver a decir "Añadir al carrito" para que ese
 * cambio cuente — de lo contrario un clic en "Ir al carrito" no añadiría nada y el número
 * mostrado dejaría de coincidir con lo que hay en el carrito real (viola el mismo principio
 * del encargo § 3 sobre nunca mostrar una cantidad que no es la enviada).
 *
 * ── Qué NO es esto ─────────────────────────────────────────────────────────────────────
 *
 * No es un segundo carrito ni un segundo endpoint: "ir al carrito" hace `.click()` sobre el
 * disparador REAL del cajón (`[data-testid="cart-drawer-trigger"]`,
 * `snippets/header-actions.liquid`) — el mismo botón que abre el header — con una única
 * salvedad de resiliencia (navegar a `Theme.routes.cart_url` si el tema no usa cajón). No
 * reescribe `assets/product-form.js` ni `assets/component.js`.
 */

import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { PICKER_SELECTOR } from './elemnt-variant-picker-bridge.js';

const GO_TO_CART_TEXT = 'IR AL CARRITO';
const PRIMARY_BUTTON_SELECTOR = 'product-form-component add-to-cart-component [ref="addToCartButton"]';

/** El botón real de "Añadir al carrito" del formulario principal, si existe en la página. */
function getPrimaryAddToCartButton() {
  const picker = document.querySelector(PICKER_SELECTOR);
  const section = picker?.closest('.shopify-section');
  const button = section?.querySelector(PRIMARY_BUTTON_SELECTOR);
  return button instanceof HTMLButtonElement ? button : null;
}

/**
 * El nodo de texto de la etiqueta — el hijo de texto inmediatamente anterior al burst de
 * check (`snippets/add-to-cart-button.liquid`: icono opcional, texto plano, `.add-to-cart__added`).
 * Ese snippet es compartido con Complete The Look y con la barra pegajosa, pero solo se lee
 * aquí sobre un botón ya confirmado como el principal (`getPrimaryAddToCartButton`).
 */
function getLabelNode(button) {
  const added = button.querySelector('.add-to-cart__added');
  const label = added?.previousSibling ?? null;
  return label?.nodeType === Node.TEXT_NODE ? label : null;
}

/** Abre el carrito reutilizando el disparador real — nunca una petición o un estado propio. */
function openCart() {
  const drawerTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
  if (drawerTrigger instanceof HTMLElement) {
    drawerTrigger.click();
    return;
  }

  // Tema sin cajón (cart_type distinto de "drawer"): el destino canónico pasa a ser la
  // página real de carrito — la misma ruta que ya usa el fallback de
  // `ProductFormComponent#refreshCart` en `assets/product-form.js`.
  window.location.assign(Theme.routes.cart_url);
}

function handleGoToCartClick(event) {
  const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
  if (button.dataset.elemntPostAdd !== 'true') return;

  event.preventDefault();
  openCart();
}

function setPostAddState(button) {
  const label = getLabelNode(button);
  if (!label) return;

  if (button.dataset.elemntOriginalLabel === undefined) {
    button.dataset.elemntOriginalLabel = label.textContent || '';
  }

  label.textContent = GO_TO_CART_TEXT;
  button.dataset.elemntPostAdd = 'true';
  button.type = 'button';
  button.removeAttribute('on:click');

  if (!button.dataset.elemntGoToCartBound) {
    button.dataset.elemntGoToCartBound = 'true';
    button.addEventListener('click', handleGoToCartClick);
  }
}

function clearPostAddState(button) {
  if (button.dataset.elemntPostAdd !== 'true') return;

  const label = getLabelNode(button);
  if (label && button.dataset.elemntOriginalLabel !== undefined) {
    label.textContent = button.dataset.elemntOriginalLabel;
  }

  button.type = 'submit';
  button.setAttribute('on:click', '/handleClick');
  delete button.dataset.elemntPostAdd;
}

/** @param {import('@shopify/events').CartLinesUpdateEvent} event */
function handleCartLinesUpdate(event) {
  if (event.action !== 'add') return;

  const target = event.target instanceof Element ? event.target : null;
  const section = target?.closest('.shopify-section');
  if (!section || !section.querySelector(PICKER_SELECTOR)) return;

  const button = getPrimaryAddToCartButton();
  if (!button) return;

  event.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      setPostAddState(button);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[elemnt-post-add-cta] Event promise rejected:', error);
    });
}

/**
 * Un cambio de cantidad tras ya haber añadido: el cliente probablemente quiere agregar más,
 * así que el CTA vuelve a "Añadir al carrito" para que ese cambio cuente (ver cabecera).
 */
function handleQuantityUpdate() {
  const button = getPrimaryAddToCartButton();
  if (button) clearPostAddState(button);
}

document.addEventListener(StandardEvents.cartLinesUpdate, handleCartLinesUpdate);
document.addEventListener(ThemeEvents.quantitySelectorUpdate, handleQuantityUpdate);
